import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import ReactDatePicker from "react-datepicker";
import {
  addLocalNotification,
  clearEmployeeAttendance,
  requestDeleteEmployeeAttendance,
  requestGetBusinessEmployee,
  requestListBusinessCalendar,
  requestListEmployeeAttendance,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../Context";
import TableSkeleton from "../../Components/TableSkeleton";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExternalLinkIcon,
  PencilAltIcon,
  PlusCircleIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import AttendanceLogModal from "../../Components/AttendanceLogModal";
import {
  cancelAPIRequest,
  currencyFormat,
  formatTime,
  generateMonthDates,
  getErrorMessages,
} from "../../Utils";
import { ATTENDANCE_STATUS_OPTIONS } from "../../Constants/business";
import Badge from "../../Components/Badge";
import { ModalAlert } from "../../Components/ModalAlert";
import { ModalConfirm } from "../../Components/ModalConfirm";
import Stats from "../../Components/v2/Stats";
import {
  calculateGrossFromNet,
  calculateGrossSalary,
  calculateLastTaxPeriod,
  calculateTERRate,
  getTERCategory,
  POSITION_COST_MAX,
  POSITION_COST_PERCENTAGE,
} from "../../Constants/terRates";
import { getAnnualisation, getTaxYearEndDate } from "../../Utils/taxSubject";
import {
  getEmployerPremiums,
  isEmployerContributionAllowance,
} from "../../Utils/employeeBenefits";
import { Alert } from "../../Components/Alert";

const EmployeeAttendances = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const monthParam = searchParams.get("month");

  const notificationDispatch = useNotificationDispatch();
  const dispatch = useBusinessTransactionDispatch();
  const {
    request,
    attendances,
    attendancesLoading,
    attendancesError,
    deletedAttendanceId,
    deleteAttendanceLoading,
    deleteAttendanceError,
    calendars,
    calendarsLoading,
    calendarsError,
    employee,
    getEmployeeLoading,
    getEmployeeError,
  } = useBusinessTransactionState();
  const { user } = useAuthState();

  const [showSalaryDetail, setShowSalaryDetail] = useState(false);
  const [attendanceLogModalOpen, setAttendanceLogModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [holidayMap, setHolidayMap] = useState({});

  const selectedMonth = dayjs(selectedDate).format("YYYY-MM");
  const hasError = attendancesError || calendarsError || getEmployeeError;

  const monthDates = useMemo(() => {
    return generateMonthDates(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
      clearEmployeeAttendance(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      requestListEmployeeAttendance(dispatch, {
        employeeID: id,
        attendanceDate: getAttendanceDate(selectedMonth),
      });
    }
  }, [dispatch, selectedMonth, id]);

  useEffect(() => {
    if (id) {
      requestGetBusinessEmployee(dispatch, { id });
    }
  }, [dispatch, id]);

  //  Handle delete success
  useEffect(() => {
    if (deletedAttendanceId) {
      // Close modal
      setShowConfirmModal(false);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-attendance-${deletedAttendanceId}`,
        type: "success",
        title: "common.success",
        body: "employees.attendances.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedAttendanceId]);

  useEffect(() => {
    if (deleteAttendanceError) {
      setShowErrorModal(true);
      setShowConfirmModal(false);
    }
  }, [deleteAttendanceError]);

  // Load calendars for holiday information
  useEffect(() => {
    if (user.mainBusiness?.id) {
      const holidayDateFilter = {
        beginsWith: selectedMonth,
      };

      requestListBusinessCalendar(dispatch, {
        businessID: user.mainBusiness.id,
        page: 1,
        limit: 10,
        holidayDateFilter,
      });
    }
  }, [dispatch, user.mainBusiness?.id, selectedMonth]);

  useEffect(() => {
    if (calendars?.length > 0) {
      const newHolidayMap = {};
      calendars.forEach((calendar) => {
        calendar.holidays?.items?.forEach((holiday) => {
          newHolidayMap[holiday.date] = holiday;
        });
      });
      setHolidayMap(newHolidayMap);
    }
  }, [calendars]);

  useEffect(() => {
    if (monthParam && dayjs(monthParam, "YYYY-MM", true).isValid()) {
      const dateFromParam = dayjs(monthParam, "YYYY-MM").toDate();
      const currentSelectedMonth = dayjs(selectedDate).format("YYYY-MM");

      if (monthParam !== currentSelectedMonth) {
        setSelectedDate(dateFromParam);
      }
    } else if (!monthParam) {
      const currentMonth = dayjs(selectedDate).format("YYYY-MM");
      navigate(`/employee/attendance/${id}?month=${currentMonth}`, {
        replace: true,
      });
    }
  }, [location.search, id, navigate, selectedDate, monthParam]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const newMonth = dayjs(date).format("YYYY-MM");
    navigate(`/employee/attendance/${id}?month=${newMonth}`, { replace: true });
  };

  const getAttendanceForDate = (date) => {
    return attendances.find((attendance) => attendance.attendanceDate === date);
  };

  const getHolidayForDate = (date) => {
    return holidayMap[date] || null;
  };

  const getDateTextClass = (date) => {
    const holiday = getHolidayForDate(date);

    if (holiday) {
      return holiday.isPaid ? "text-red-500" : "text-yellow-500";
    }

    if (calendars?.length > 0) {
      const dayOfWeek = dayjs(date).day();
      const dayKey = dayOfWeek.toString();

      for (const calendar of calendars) {
        if (calendar.workWeekPattern) {
          const pattern = JSON.parse(calendar.workWeekPattern);
          if (pattern[dayKey] === false) {
            return "text-red-500";
          }
        }
      }
    }

    return "text-gray-700";
  };

  const isWorkingDay = (date) => {
    const holiday = getHolidayForDate(date);

    if (holiday) {
      return false;
    }

    if (calendars?.length > 0) {
      const dayOfWeek = dayjs(date).day();
      const dayKey = dayOfWeek.toString();

      for (const calendar of calendars) {
        if (calendar.workWeekPattern) {
          const pattern = JSON.parse(calendar.workWeekPattern);
          return pattern[dayKey] !== false;
        }
      }
    }

    const dayOfWeek = dayjs(date).day();
    return dayOfWeek >= 1 && dayOfWeek <= 5;
  };

  const isLateAttendance = (attendance) => {
    if (
      !attendance ||
      !attendance.clockInTime ||
      !employee?.standardWorkStartTime
    ) {
      return false;
    }

    const clockInTime = dayjs(`2000-01-01 ${attendance.clockInTime}`);
    const standardStartTime = dayjs(
      `2000-01-01 ${employee.standardWorkStartTime}`
    );

    return clockInTime.isAfter(standardStartTime);
  };

  const calculateAttendanceStats = () => {
    const totalDaysInMonth = monthDates.length;

    const workingDays = monthDates.filter((date) => isWorkingDay(date)).length;

    let absenceDays = 0;
    monthDates.forEach((date) => {
      if (isWorkingDay(date)) {
        const attendance = getAttendanceForDate(date);
        if (
          !attendance ||
          attendance.status === "ABSENT" ||
          attendance.status === "UNPAID_LEAVE"
        ) {
          absenceDays++;
        }
      }
    });

    let lateDays = 0;
    attendances.forEach((attendance) => {
      if (isLateAttendance(attendance)) {
        lateDays++;
      }
    });

    const absencePercentage =
      workingDays > 0 ? Math.round((absenceDays / workingDays) * 100) : 0;

    return {
      totalWorkingDays: workingDays,
      totalDaysInMonth,
      absenceDays,
      absencePercentage,
      lateDays,
    };
  };

  const calculateMonthlySalaryDetail = useMemo(() => {
    if (!employee?.baseSalary || !employee?.startDate) return null;

    const currentMonth = dayjs(selectedDate);
    const startDate = dayjs(employee.startDate);
    // Payroll stops at the last tax period, which the end of the subjective
    // tax liability can bring forward from the end of employment.
    const endDate = getTaxYearEndDate(employee);

    const isBeforeStartDate = currentMonth.isBefore(startDate, "month");
    const isAfterEndDate = endDate && currentMonth.isAfter(endDate, "month");

    if (isBeforeStartDate || isAfterEndDate) return null;

    const stats = calculateAttendanceStats();
    const attendanceDeductionPercentage = stats.absencePercentage / 100;
    const attendanceDeduction =
      employee.baseSalary * attendanceDeductionPercentage;
    const adjustedBaseSalary = employee.baseSalary - attendanceDeduction;

    // Same bruto as the Salary Table: a stored JKK/JKM allowance is replaced by
    // the premium derived from upah below, never added on top of it.
    const taxableAllowances =
      employee?.allowances?.items?.filter(
        (a) => a.isTaxable && !isEmployerContributionAllowance(a)
      ) || [];

    const monthlyAllowances = taxableAllowances.filter(
      (a) => a.frequency === "MONTHLY"
    );

    const oneTimeAllowances = taxableAllowances.filter(
      (a) =>
        a.frequency !== "MONTHLY" &&
        dayjs(a.startDate).format("YYYY-MM") === currentMonth.format("YYYY-MM")
    );

    const monthlyAllowanceAmount = monthlyAllowances.reduce(
      (sum, a) => sum + a.amount,
      0
    );

    const oneTimeAllowanceAmount = oneTimeAllowances.reduce(
      (sum, a) => sum + a.amount,
      0
    );

    // BPJS Kesehatan 4%, JKK and JKM paid by the employer, priced on upah.
    const employerPremiumAmount = getEmployerPremiums(employee).total;

    const totalTaxableAllowances =
      monthlyAllowanceAmount + oneTimeAllowanceAmount + employerPremiumAmount;

    const isGrossUp = employee.taxMethod === "GROSS_UP";
    const isDecember = currentMonth.month() === 11;
    const isLastMonth = endDate && currentMonth.isSame(endDate, "month");
    const isProgressiveTaxMonth = isDecember || isLastMonth;

    const terCategory = getTERCategory(
      employee.marriageStatus,
      employee.dependent
    );

    // This page shows a single month and has no yearly accumulator, so yearly
    // gross is approximated by repeating the current month. The repetition is
    // bounded by actual months of employment: someone who started in September
    // is counted over four months, not twelve. The figure stays an estimate;
    // the Salary Table is what calculates it properly.
    const taxYear = currentMonth.year();
    const employeeStart = dayjs(employee.startDate);
    const firstMonth =
      employeeStart.year() === taxYear
        ? employeeStart
        : dayjs(`${taxYear}-01-01`);
    const monthsSoFar = Math.min(
      Math.max(currentMonth.diff(firstMonth.startOf("month"), "month") + 1, 1),
      12
    );
    const priorMonths = monthsSoFar - 1;

    const { annualise, monthsWorked } = getAnnualisation(employee, taxYear);

    const netBasisGross = calculateGrossSalary(
      adjustedBaseSalary,
      totalTaxableAllowances
    );
    // Earlier months were withheld through TER. Under gross-up their gross
    // already contains the tax allowance; under gross it does not.
    const priorGross = isGrossUp
      ? calculateGrossFromNet(
          adjustedBaseSalary,
          totalTaxableAllowances,
          terCategory
        )
      : netBasisGross;
    const priorTaxPaid =
      priorGross * calculateTERRate(priorGross, terCategory) * priorMonths;

    const monthlyPositionCost = (gross) =>
      Math.min(gross * POSITION_COST_PERCENTAGE, POSITION_COST_MAX / 12);

    let grossSalary, monthlyTax;

    if (isProgressiveTaxMonth) {
      ({ monthlyTax } = calculateLastTaxPeriod({
        // The last tax period is recorded on a net basis: its tax allowance
        // is the unknown being solved for.
        yearlyGrossSalary: priorGross * priorMonths + netBasisGross,
        yearlyPositionCost:
          monthlyPositionCost(priorGross) * priorMonths +
          monthlyPositionCost(netBasisGross),
        yearlyDeductions: 0,
        yearlyTaxPaid: priorTaxPaid,
        marriageStatus: employee.marriageStatus,
        dependent: employee.dependent,
        isGrossUp,
        annualise,
        monthsWorked,
      }));
    } else {
      const terGross = isGrossUp ? priorGross : netBasisGross;
      monthlyTax = terGross * calculateTERRate(terGross, terCategory);
    }

    grossSalary = isGrossUp
      ? adjustedBaseSalary + monthlyTax + totalTaxableAllowances
      : netBasisGross;

    const netSalary = grossSalary - monthlyTax;
    const terRate = calculateTERRate(grossSalary, terCategory);

    return {
      baseSalary: employee.baseSalary,
      attendanceDeduction,
      adjustedBaseSalary,
      totalTaxableAllowances,
      grossSalary: Math.floor(grossSalary),
      terCategory,
      terRate,
      monthlyTax: Math.floor(monthlyTax),
      netSalary: Math.floor(netSalary),
      isGrossUp,
      isProgressiveTaxMonth,
      attendanceStats: stats,
      isLastMonth: isDecember || isLastMonth,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, selectedDate, attendances, calendars]);

  const getAttendanceDate = (month) => ({
    beginsWith: month,
  });

  const handleClose = () => {
    cancelAPIRequest(request);
    setAttendanceLogModalOpen(false);
    setSelectedAttendance(null);
    clearEmployeeAttendance(dispatch);
  };

  const handleEdit = (attendance) => {
    setSelectedAttendance(attendance);
    setAttendanceLogModalOpen(true);
  };

  const handleDelete = (attendance) => {
    setSelectedAttendance(attendance);
    setShowConfirmModal(true);
  };

  const onDelete = () => {
    requestDeleteEmployeeAttendance(dispatch, {
      id: selectedAttendance?.id,
    });
  };

  const getStatusLabel = (statusValue) => {
    const status = ATTENDANCE_STATUS_OPTIONS.find(
      (option) => option.value === statusValue
    );
    return status ? status.label : statusValue;
  };

  const getStatusBadgeType = (status) => {
    switch (status) {
      case "PRESENT":
        return "success";
      case "ABSENT":
        return "danger";
      case "SICK_LEAVE":
      case "MEDICAL_LEAVE":
      case "MATERNITY_LEAVE":
      case "PATERNITY_LEAVE":
      case "COMPASSIONATE_LEAVE":
        return "alert";
      case "ANNUAL_LEAVE":
      case "PERSONAL_LEAVE":
      case "EMERGENCY_LEAVE":
      case "UNPAID_LEAVE":
        return "secondary";
      case "REMOTE_WORK":
      case "BUSINESS_TRIP":
      case "TRAINING":
      case "CONFERENCE":
      case "CLIENT_MEETING":
      case "COMPANY_EVENT":
        return "info";
      default:
        return "primary";
    }
  };

  const stats = calculateAttendanceStats();

  const attendanceStats = [
    {
      title: t("employees.attendances.stats.totalWorkingDays"),
      value: stats.totalWorkingDays,
      footer: t("employees.attendances.stats.fromDays", {
        count: stats.totalDaysInMonth,
      }),
    },
    {
      title: t("employees.attendances.stats.absenceDays"),
      value: stats.absenceDays,
      footer: `${stats.absencePercentage}%`,
    },
    {
      title: t("employees.attendances.stats.lateDays"),
      value: stats.lateDays,
    },
  ];

  const SkeletonStats = () => {
    return (
      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="px-4 py-5 shadow rounded-lg overflow-hidden sm:p-6 animate-pulse"
          >
            <dt className="text-sm font-medium text-gray-500 truncate">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              <div className="h-8 bg-gray-300 rounded w-1/2"></div>
            </dd>
            <dd className="mt-2 flex items-center text-sm text-gray-600">
              <div className="h-4 bg-gray-300 rounded w-1/3"></div>
            </dd>
          </div>
        ))}
      </dl>
    );
  };

  const ErrorStats = ({ error }) => {
    return (
      <div className="bg-white min-h-full rounded-lg">
        <Alert
          type="error"
          title="Error"
          message={error.message}
          errors={error.errors}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Attendance Log Modal */}
      {attendanceLogModalOpen && (
        <AttendanceLogModal
          open={attendanceLogModalOpen}
          onClose={handleClose}
          attendance={selectedAttendance}
          calendars={calendars}
          employee={employee}
        />
      )}

      {/* Delete Modal */}
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setShowConfirmModal(open);
        }}
        title="employees.attendances.modal.title.delete"
        description="employees.attendances.modal.description.delete"
        confirmButtonText="common.yes"
        cancelButtonText="common.cancel"
        loadingButton={deleteAttendanceLoading}
        disabledButton={deleteAttendanceLoading}
        onConfirm={onDelete}
        onCancel={() => {
          cancelAPIRequest(request);
          setShowConfirmModal(false);
        }}
      />

      {/* Error Modal */}
      {deleteAttendanceError && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setShowErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(deleteAttendanceError)}
          onConfirm={() => clearEmployeeAttendance(dispatch)}
        />
      )}

      {/* Stats */}
      <div className="bg-white p-4 rounded-lg">
        {attendancesLoading || getEmployeeLoading || calendarsLoading ? (
          <SkeletonStats />
        ) : hasError ? (
          <ErrorStats error={hasError} />
        ) : (
          <Stats stats={attendanceStats} showCurrency={false} />
        )}
      </div>

      {!hasError ? (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
            <div>
              <ReactDatePicker
                className="block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500 cursor-pointer"
                selected={selectedDate}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                onChange={handleDateChange}
              />
            </div>
          </div>
          <div className="flex flex-col">
            <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.attendances.fields.date")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.attendances.fields.clockIn")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.attendances.fields.clockOut")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.attendances.fields.status")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.attendances.fields.notes")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("common.actions.label")}
                        </th>
                      </tr>
                    </thead>
                    {attendancesLoading || calendarsLoading ? (
                      <TableSkeleton rows={3} columns={6} />
                    ) : (
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {monthDates.map((date) => {
                          const attendance = getAttendanceForDate(date);
                          const textColorClass = getDateTextClass(date);

                          return (
                            <tr key={date}>
                              <td
                                className={`px-4 py-2 text-sm whitespace-nowrap ${textColorClass}`}
                              >
                                {dayjs(date)
                                  .locale(i18n.language)
                                  .format("D MMMM YYYY")}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                                {attendance
                                  ? formatTime(attendance.clockInTime)
                                  : "-"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                                {attendance
                                  ? formatTime(attendance.clockOutTime)
                                  : "-"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                                {attendance ? (
                                  <Badge
                                    type={getStatusBadgeType(attendance.status)}
                                    title={getStatusLabel(attendance.status)}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                                {attendance ? attendance.notes || "-" : "-"}
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {attendance ? (
                                  <div className="flex flex-auto gap-2">
                                    <button
                                      onClick={() => handleEdit(attendance)}
                                    >
                                      <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(attendance)}
                                    >
                                      <TrashIcon className="h-5 w-5 text-red-500" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex flex-auto gap-2">
                                    {(!employee?.endDate ||
                                      dayjs(date).isSameOrBefore(
                                        dayjs(employee.endDate)
                                      )) && (
                                      <button
                                        onClick={() => {
                                          setSelectedAttendance({
                                            attendanceDate: date,
                                          });
                                          setAttendanceLogModalOpen(true);
                                        }}
                                        className="text-blue-500 hover:text-blue-700"
                                      >
                                        <PlusCircleIcon className="h-5 w-5" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {monthDates.length === 0 && (
                          <tr>
                            <td className="p-4" colSpan={6}>
                              {attendancesError || calendarsError ? (
                                <ErrorList
                                  message={
                                    (attendancesError || calendarsError)
                                      ?.message || "Error fetching attendances."
                                  }
                                  errors={
                                    (attendancesError || calendarsError)?.errors
                                  }
                                />
                              ) : (
                                <EmptyList title="No Data" />
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Salary Detail Section */}
          {calculateMonthlySalaryDetail &&
            employee?.employmentType === "PERMANENT" &&
            employee?.salaryType === "MONTHLY" && (
              <div className="bg-white rounded-lg border border-gray-200">
                <div
                  className={`p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 ${
                    showSalaryDetail ? "border-b" : ""
                  }`}
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-baseline gap-2">
                      <h3 className="text-base font-medium text-gray-900">
                        {t("employees.salaries.table.header.detailTitle")}
                      </h3>
                    </div>
                    <div className="text-base text-gray-900">
                      {currencyFormat(calculateMonthlySalaryDetail.grossSalary)}
                    </div>
                  </div>
                  <div className="flex items-center justify-end">
                    <button
                      onClick={() => setShowSalaryDetail(!showSalaryDetail)}
                      className="flex items-center text-gray-900 p-1 sm:p-0"
                    >
                      {showSalaryDetail ? (
                        <ChevronUpIcon className="h-5 w-5 sm:h-5 sm:w-5" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 sm:h-5 sm:w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {showSalaryDetail && (
                  <div className="space-y-4 p-4">
                    <div className="flex flex-col gap-4">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">
                          {t("employees.salaries.table.header.salary")}
                        </span>
                        <span className="text-sm text-gray-600">
                          {currencyFormat(
                            calculateMonthlySalaryDetail.baseSalary
                          )}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm font-medium">
                          {t(
                            "employees.salaries.table.header.attendanceDeduction"
                          )}{" "}
                          (
                          {
                            calculateMonthlySalaryDetail.attendanceStats
                              .absencePercentage
                          }
                          %)
                        </span>
                        <span className="text-sm text-gray-600">
                          -
                          {currencyFormat(
                            calculateMonthlySalaryDetail.attendanceDeduction
                          )}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm font-medium">
                          {t("employees.salaries.table.header.totalAllowances")}
                        </span>
                        <span className="text-sm text-gray-600">
                          {currencyFormat(
                            calculateMonthlySalaryDetail.totalTaxableAllowances
                          )}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm font-medium">
                          {t("employees.salaries.table.header.grossSalary")}
                        </span>
                        <span className="text-sm text-gray-600">
                          {currencyFormat(
                            calculateMonthlySalaryDetail.grossSalary
                          )}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm font-medium">
                          {t("employees.salaries.table.header.terCategory")}
                        </span>
                        <span className="text-sm text-gray-600">
                          {calculateMonthlySalaryDetail.isLastMonth
                            ? "-"
                            : `TER ${calculateMonthlySalaryDetail.terCategory}`}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm font-medium">
                          {t("employees.salaries.table.header.terRate")}
                        </span>
                        <span className="text-sm text-gray-600">
                          {calculateMonthlySalaryDetail.isLastMonth
                            ? "-"
                            : `${(
                                calculateMonthlySalaryDetail.terRate * 100
                              ).toFixed(2)}
                    %`}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm font-medium">
                          {t("employees.salaries.table.header.tax")}
                        </span>
                        <span className="text-sm text-gray-600">
                          {currencyFormat(
                            calculateMonthlySalaryDetail.monthlyTax
                          )}
                        </span>
                      </div>
                    </div>
                    
                    {/* Open Salary Table Button */}
                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={() =>
                          window.open(`/employee/salary/${employee.id}`, "_blank")
                        }
                        className="w-full flex items-center justify-center text-blue-600 hover:text-blue-800 gap-2 text-sm font-medium py-2 px-4 rounded-lg border border-blue-200 hover:border-blue-300 bg-blue-50 hover:bg-blue-100 transition-colors"
                        title={t("employees.salaries.openInNewTab")}
                      >
                        <span>{t("employees.salaries.viewSalaryTable")}</span>
                        <ExternalLinkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
        </>
      ) : null}
    </div>
  );
};

export default EmployeeAttendances;
